import React, { useEffect, useState } from "react";
import { GlobalService } from "../../../shared";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import { createUserEducation, getUserEducation, UpdateUserEducation, useAppDispatch } from "../../../../redux";
import NewEducationModal from "../../modals/NewEducationModal";
import { Edit, Plus } from "lucide-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

export default function Education({ userId, showEditIcon = true }: { userId?: string; showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const { education } = useSelector((state: any) => state.professional);
    const { user } = useSelector((state: any) => state.auth);
    const [showEducationModal, setShowEducationModal] = useState(false);
    const [editEducationData, setEditEducationData] = useState(null);
    const dispatch = useAppDispatch();
    const [allEducationData, setAllEducationData] = useState([]);
    const [originalAllEducationData, setOriginalAllEducationData] = useState([]);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user

    useEffect(() => {
        if (!education) return;

        const transformedEducation = education.map((edu: any) => ({
            id: edu.id,
            degree: edu.degree,
            school: edu.school,
            year: new Date(edu.createdAt).getFullYear().toString(), // or use edu.year if available directly
            createdAt: edu.createdAt,
            updatedAt: edu.updatedAt,
            userId: edu.userId,
        }));
        setOriginalAllEducationData(transformedEducation);
        setAllEducationData(transformedEducation);
    }, [education]);
    useEffect(() => {
        getAllEducations();
    }, [targetUserId]);

    const getAllEducations = async () => {
        await dispatch(getUserEducation({ id: targetUserId }));
    };
    const handleEducationSave = async (data: any[]) => {
        const newItems = data.filter(item => !item.id);
        const existingItems = data.filter(item => item.id);
        let updatedCount = 0;

        // Update changed existing items
        for (const item of existingItems) {
            const original = originalAllEducationData.find(e => e.id === item.id);

            const hasChanged =
                item.degree !== original?.degree || item.school !== original?.school || item.year !== original?.year;
            if (!hasChanged) continue;

            const updatePayload = {
                userId: { id: targetUserId },
                degree: item.degree,
                school: item.school,
                year: Number(item.year),
            };

            await dispatch(UpdateUserEducation({ id: item.id, data: updatePayload }));
            updatedCount++;
        }

        // Create new items
        let createdItems = [];
        if (newItems.length > 0) {
            const createPayload = newItems.map(item => ({
                userId: { id: targetUserId },
                degree: item.degree,
                school: item.school,
                year: Number(item.year),
            }));

            const result = await dispatch(createUserEducation({ data: createPayload }));

            if (createUserEducation.fulfilled.match(result)) {
                createdItems = result.payload;
            }
        }

        // Merge updated and newly created items
        const updatedData = [...existingItems, ...createdItems];

        setAllEducationData(updatedData);
        setOriginalAllEducationData(updatedData); // sync originals for future comparisons
        if (updatedCount > 0 && newItems.length === 0) {
            GlobalService.showToast("success", t("education.successTitle"), t("education.updateSuccess"));
        }
    };

    return (
        <>
            <Card color={undefined} className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-lg font-semibold">{t("education.title")}</h2>
                    {showEditIcon && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                setEditEducationData(allEducationData);
                                setShowEducationModal(true);
                            }}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="space-y-4">
                    {(allEducationData || []).map((edu, index) => (
                        <div key={index} className="border-l-2 border-gray-200 pl-4 py-2">
                            <h3 className="font-medium text-gray-900">{edu.degree}</h3>
                            <p className="text-sm text-gray-600">{edu.school}</p>
                            <p className="text-sm text-gray-500">{edu.year}</p>
                        </div>
                    ))}
                </div>
            </Card>
            <NewEducationModal
                open={showEducationModal}
                onClose={() => {
                    setShowEducationModal(false);
                }}
                onSave={handleEducationSave}
                initialData={editEducationData} // <-- edit mode
            />
        </>
    );
}
